import { UnifiedThemeProvider } from '@backstage/theme';
import { useEffect, type ReactNode } from 'react';
import { cfaLightTheme, cfaDarkTheme } from './cfaTheme';
import { cfaLightThemeBuiCss } from './lightThemeBui';
import { cfaDarkThemeBuiCss } from './darkThemeBui';

/**
 * Sets CFA `data-color-mode` / `data-theme-mode` on `<body>` for BUI token selectors
 * (`body[data-theme-mode='…']` in `lightThemeBui` / `darkThemeBui`).
 *
 * UnifiedThemeProvider also writes `data-theme-mode`, but passive effects from nested
 * providers can run in an order that leaves the body marker stale. A zero-delay timeout
 * runs after that flush so body markers match the active CFA subtree.
 */
function useCfaBodyMarkers(mode: 'light' | 'dark') {
  useEffect(() => {
    const id = window.setTimeout(() => {
      document.body.setAttribute('data-theme-mode', mode);
      document.body.setAttribute('data-color-mode', mode);
    }, 0);

    return () => {
      window.clearTimeout(id);
      document.body.removeAttribute('data-color-mode');
      document.body.removeAttribute('data-theme-mode');
    };
  }, [mode]);
}

export function CfaLightThemeProvider({ children }: { children: ReactNode }) {
  useCfaBodyMarkers('light');
  return (
    <UnifiedThemeProvider theme={cfaLightTheme}>
      <style>{cfaLightThemeBuiCss}</style>
      {children}
    </UnifiedThemeProvider>
  );
}

export function CfaDarkThemeProvider({ children }: { children: ReactNode }) {
  useCfaBodyMarkers('dark');
  return (
    <UnifiedThemeProvider theme={cfaDarkTheme}>
      <style>{cfaDarkThemeBuiCss}</style>
      {children}
    </UnifiedThemeProvider>
  );
}
